import type { UnitsSettings } from "../../../app/units/units";
import { Checkbox, App } from "../../../../../../../shared/frontend/ui";
import { CheckCircleOutlined,CloseCircleOutlined,SaveOutlined } from "@ant-design/icons";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { Button, Card, Flex, Form, Grid, Input, Select, Space, Tooltip } from "../../../../../../../shared/frontend/ui";
import { spacing } from "../../../app/design-system";
import React from "react";
import type { useAuth } from "../../../app/auth/useAuth";
import type { useI18n } from "../../../app/i18n/useI18n";
import { formatDateOnly } from "../../../app/units/format";
import { emailBrowserAutofillInputProps, INTERNAL_FORM_AUTOCOMPLETE, usePersonAutofillFieldProps } from "../../../app/utils/browserAutofill";
import { dateOfBirthFieldContract } from "../../../app/utils/dateOfBirthValidation";
import { isValidEmail,isValidPhone } from "../../../app/utils/validation";
import type { PersonDto } from "../types";
import { PersonBankDetailsSection } from "./PersonBankDetailsSection";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { SecretInput } from "../../../components/SecretInput";

type MessageApi = {
  success: (content: string) => void;
  error: (content: string) => void;
};

type Props = {
  loading: boolean;
  form: FormInstance;
  person: PersonDto | null;
  personId: string | undefined;
  canWritePerson: boolean;
  canWriteFinance: boolean;
  saving: boolean;
  savePerson: () => void | Promise<void>;
  auth: ReturnType<typeof useAuth>;
  message: MessageApi;
  modal: { confirm: ReturnType<typeof App.useApp>["modal"]["confirm"] };
  text: ReturnType<typeof useI18n>["text"];
  units: UnitsSettings;
  dateInputFormat: string | string[];
  load: () => Promise<void>;
  setPersonBankName: (value: string | null) => void;
};

export function PersonProfileTab({
  loading,
  form,
  person,
  personId,
  canWritePerson,
  canWriteFinance,
  saving,
  savePerson,
  auth,
  message,
  modal,
  text,
  units,
  dateInputFormat,
  load,
  setPersonBankName
}: Props) {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const dateOfBirthContract = dateOfBirthFieldContract(text);
  // Wie beim Anlegen: alle Personenfelder bereits beim Rendern schützen, nicht erst per DOM-Observer.
  const { inputProps: personInputProps, labelHtmlFor: personLabelHtmlFor } = usePersonAutofillFieldProps();

  const contactRestrictionControls = (
    <>
      <Form.Item name="doNotContactMessages" noStyle valuePropName="checked">
        <Checkbox>{text("No messages")}</Checkbox>
      </Form.Item>
      <Form.Item name="doNotContactCalls" noStyle valuePropName="checked">
        <Checkbox>{text("No calls")}</Checkbox>
      </Form.Item>
    </>
  );

  return (
    <Card>
      <LoadingOverlay visible={loading}>
      <Form form={form} layout="vertical" requiredMark={false} disabled={!canWritePerson} autoComplete={INTERNAL_FORM_AUTOCOMPLETE} onFinish={savePerson}>
        {canWritePerson && (
          <Flex
            className="person-profile-action-bar"
            justify="flex-end"
            align={isMobile ? "stretch" : "center"}
            vertical={isMobile}
            wrap={isMobile ? false : "wrap"}
            gap={isMobile ? 10 : 16}
            style={{ marginBottom: spacing.xl, width: "100%" }}
          >
            {isMobile ? (
              contactRestrictionControls
            ) : (
              <Flex wrap="wrap" justify="flex-end" align="center" gap={16}>
                {contactRestrictionControls}
              </Flex>
            )}
            <Flex className="person-profile-save-action" justify="flex-end">
              <Button type="primary" icon={<SaveOutlined />} loading={saving} htmlType="submit">
                {text("Save")}
              </Button>
            </Flex>
          </Flex>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: spacing.md }}>
          <Form.Item name="title" htmlFor={personLabelHtmlFor.title} label={text("Title")}>
            <Input placeholder={text("e.g. Dr., Prof.")} {...personInputProps.title} />
          </Form.Item>
          <Form.Item name="useDu" label={text("Form of address (formal/informal)")}>
            <Select
              allowClear
              options={[
                { value: false, label: text("Formal") },
                { value: true, label: text("Informal") }
              ]}
              placeholder={text("(optional)")}
            />
          </Form.Item>
          <Form.Item name="gender" label={text("Gender")}>
            <Select
              allowClear
              options={[
                { value: 1, label: text("Male") },
                { value: 2, label: text("Female") },
                { value: 3, label: text("Diverse") }
              ]}
            />
          </Form.Item>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: spacing.md }}>
          <Form.Item
            name="firstName"
            htmlFor={personLabelHtmlFor.firstName}
            label={text("First name")}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, v) => {
                  const other = String(getFieldValue("lastName") || "").trim();
                  const me = String(v || "").trim();
                  if (me || other) return;
                  throw new Error(text("First name or last name required"));
                }
              })
            ]}
          >
            <Input {...personInputProps.firstName} />
          </Form.Item>
          <Form.Item
            name="lastName"
            htmlFor={personLabelHtmlFor.lastName}
            label={text("Last name")}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, v) => {
                  const other = String(getFieldValue("firstName") || "").trim();
                  const me = String(v || "").trim();
                  if (me || other) return;
                  throw new Error(text("First name or last name required"));
                }
              })
            ]}
          >
            <Input {...personInputProps.lastName} />
          </Form.Item>
          <Form.Item name="dateOfBirth" label={text("Date of birth")} rules={dateOfBirthContract.rules}>
            <DatePicker
              allowClear
              placeholder={text("Select date")}
              format={dateInputFormat}
              minDate={dateOfBirthContract.minDate}
              maxDate={dateOfBirthContract.maxDate}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>

        <div style={{ marginTop: spacing.xl }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: spacing.md }}>
            <Form.Item name="street" htmlFor={personLabelHtmlFor.street} label={text("Street")}>
              <Input {...personInputProps.street} />
            </Form.Item>
            <Form.Item name="zip" htmlFor={personLabelHtmlFor.zip} label={text("ZIP")}>
              <Input {...personInputProps.zip} />
            </Form.Item>
            <Form.Item name="city" htmlFor={personLabelHtmlFor.city} label={text("City")}>
              <Input {...personInputProps.city} />
            </Form.Item>
            <Form.Item name="country" htmlFor={personLabelHtmlFor.country} label={text("Country")}>
              <Input {...personInputProps.country} />
            </Form.Item>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: spacing.md, marginTop: spacing.md }}>
            <Form.Item
              name="phone"
              htmlFor={personLabelHtmlFor.phone}
              label={text("Phone")}
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  validator: async (_, v) => {
                    const s = String(v || "").trim();
                    if (!s) return;
                    if (!isValidPhone(s)) throw new Error(text("Invalid phone number"));
                  }
                }
              ]}
            >
              {person?.doNotContactCalls && (person?.phone ?? "").trim() ? (
                <SecretInput visibilityToggle {...personInputProps.phone} />
              ) : (
                <Input {...personInputProps.phone} />
              )}
            </Form.Item>
            <Form.Item
              name="mobile"
              htmlFor={personLabelHtmlFor.mobile}
              label={
                <Space size="small">
                  {text("Mobile")}
                  {person != null && person.mobileWhatsAppStatus !== undefined && person.mobileWhatsAppStatus !== null && (
                    <Tooltip title={person.mobileWhatsAppStatus ? text("WhatsApp available") : text("No WhatsApp")}>
                      {person.mobileWhatsAppStatus ? <CheckCircleOutlined style={{ color: "var(--mq-kit-ok)" }} /> : <CloseCircleOutlined style={{ color: "var(--mq-theme-signal)" }} />}
                    </Tooltip>
                  )}
                </Space>
              }
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  validator: async (_, v) => {
                    const s = String(v || "").trim();
                    if (!s) return;
                    if (!isValidPhone(s)) throw new Error(text("Invalid phone number"));
                  }
                }
              ]}
            >
              {person?.doNotContactCalls && (person?.mobile ?? "").trim() ? (
                <SecretInput visibilityToggle {...personInputProps.mobile} />
              ) : (
                <Input {...personInputProps.mobile} />
              )}
            </Form.Item>
            <Form.Item
              name="email"
              htmlFor={personLabelHtmlFor.email}
              label={text("Email")}
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  validator: async (_, v) => {
                    const s = String(v || "").trim();
                    if (!s) return;
                    if (!isValidEmail(s)) throw new Error(text("Invalid email"));
                  }
                }
              ]}
            >
              <Input {...emailBrowserAutofillInputProps(personInputProps.email)} />
            </Form.Item>
          </div>
        </div>

        <PersonBankDetailsSection
          person={person}
          personId={personId}
          canWriteFinance={canWriteFinance}
          auth={auth}
          message={message}
          form={form}
          modal={modal}
          text={text}
          units={units}
          dateInputFormat={dateInputFormat}
          formatDateOnly={formatDateOnly}
          load={load}
          setPersonBankName={setPersonBankName}
        />
      </Form>
      </LoadingOverlay>
    </Card>
  );
}
